/**
 * decimal-exam — 小數考卷(第 5 單元 小數)線上互動卷 + 純計分函式。
 *
 * 對應 circle-exam.ts / mul-div-exam.ts / time-exam.ts 同一套形狀:分大題作答 → 交卷
 * 即時計分 + 逐題對錯 + 正解 + 錯題回流。純計分 `scoreDecimalExam` / 答案正規化
 * `normalizeDecimalAnswer` 抽出可單測(無 DOM 依賴)。
 *
 * 題型差異:
 *   - convert / addsub / apply(互換 / 加減 / 應用)= 文字輸入,寬鬆比對(全形→半形、
 *     去空白、單位寬鬆、`3/10` 分數比對)。
 *   - figure(看圖 / 數線)= 文字輸入 + **視覺圖示**(塗色格 / 數線),寬鬆數值比對。
 *   - compare(比大小)= 選項按鈕(點 >、<、=)。
 *
 * **浮點防呆**:比對一律先 normalize 成字串再比;不做瀏覽器浮點加減(答案由 decimal-gen
 * 的十分位整數 recompute 產生,本檔只比字串)。學生寫「1」與「1.0」都算對(去尾零)。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕。
 * 專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type { DecimalExam, DecimalSection, DecimalQuestion } from './decimal-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個小數答案字串以利寬鬆比對:
 *   - 全形數字 / 字母 → 半形。
 *   - 去單位「公分 / 公升 / 公斤 / 公尺 / 張 / 杯 / cm」。
 *   - 去所有空白。
 *   - **小數尾零正規化**:'1.0' → '1'、'2.50' → '2.5'(只比數值,讓 1 與 1.0 等價)。
 *   - 分數 'n/10' 保留(供分數題比對)。
 * 例:'0.7 公分' → '0.7';'1.0' → '1';'3/10' → '3/10';'＞' → '>'。
 */
export function normalizeDecimalAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  // 全形數字 → 半形。
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  // 全形英文字母 → 半形(供 cm)。
  s = s.replace(/[Ａ-Ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff21 + 0x41));
  s = s.replace(/[ａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff41 + 0x61));
  // 全形比較符號 → 半形。
  s = s.replace(/[＞﹥]/g, '>').replace(/[＜﹤]/g, '<').replace(/[＝]/g, '=');
  // 全形句點 / 中文頓點 → 半形小數點(學生常打全形「．」當小數點)。
  s = s.replace(/[．。]/g, '.');
  // 全形斜線 / 除號 → 半形(供分數 n/10)。
  s = s.replace(/[／÷]/g, '/');
  // 去單位。
  s = s.replace(/公分|公升|公斤|公尺|張|杯|cm/gi, '');
  // 去所有空白。
  s = s.replace(/\s+/g, '');
  // 小數尾零正規化(僅當是純小數,不動分數 n/10)。
  if (/^\d+\.\d+$/.test(s)) {
    s = s.replace(/\.?0+$/, ''); // '1.0'→'1'、'2.50'→'2.5'、'2.00'→'2'
    if (s === '') s = '0';
  }
  return s;
}

/**
 * 一格是否答對(寬鬆比對):
 *   1. 比大小題(正解 >/</=)→ 正規化後整段相符。
 *   2. 分數題(正解 n/10)→ 去空白後相符(也接受學生少寫斜線等的容錯交由 normalize)。
 *   3. 小數 / 應用題(正解含數值 + 可能單位)→ 去單位 + 尾零後數值相符
 *      (學生寫「0.7」「0.7 公分」「.7」都算對)。
 */
export function isDecimalAnswerCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  let s = normalizeDecimalAnswer(studentRaw);
  const c = normalizeDecimalAnswer(correct);
  if (s === '') return false;
  // 容錯:學生寫「.7」當「0.7」。
  if (/^\.\d+$/.test(s)) s = `0${s}`;
  return s === c;
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生整卷作答:`"sectionIdx:questionIdx" → 字串`。 */
export type DecimalExamAnswers = Record<string, string>;

/** 單題判定結果。 */
export interface DecimalItemResult {
  itemId: string;
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  points: number;
  earned: number;
}

/** 整卷計分結果。 */
export interface DecimalExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: DecimalItemResult[];
}

/**
 * 純計分:逐題批改(每題一格)。可單測(無 DOM 依賴)。
 *
 * @param exam    一份小數考卷。
 * @param answers 整卷作答(`"sectionIdx:questionIdx" → 字串`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreDecimalExam(exam: DecimalExam, answers: DecimalExamAnswers): DecimalExamScore {
  const results: DecimalItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    section.questions.forEach((q, qIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const studentRaw = answers[`${sIdx}:${qIdx}`];
      const ok = isDecimalAnswerCorrect(studentRaw, q.answer);
      const earned = ok ? section.pointsEach : 0;
      if (ok) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: q.id,
        correct: ok,
        correctLabel: q.answer,
        chosenLabel: studentRaw && studentRaw.trim() !== '' ? studentRaw.trim() : '',
        points: section.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/** 交卷結果回呼:錯題(供回流錯題本)。 */
export interface DecimalExamWrong {
  question: DecimalQuestion;
  sectionKind: DecimalSection['kind'];
}

/**
 * Render 線上小數考卷 into `container`。每大題列題 + 填答格 / 選項(+ 看圖 / 數線視覺)
 * → 「交卷」即時計分 + 逐題對錯。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)小數考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runDecimalExam(
  container: HTMLElement,
  exam: DecimalExam,
  onDone: () => void,
  onSubmit?: (score: DecimalExamScore, wrong: DecimalExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 在每格寫出答案或點選,作答後按「交卷」看分數與正解。`,
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'decimal-exam';
  root.setAttribute('data-testid', 'decimal-exam');
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  const answers: DecimalExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `dec-section dec-section--${section.kind}`;
    sectionEl.setAttribute('data-testid', `dec-section-${section.kind}`);
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'dec-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'dec-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    section.questions.forEach((q, qIdx) => {
      sectionEl.appendChild(renderQuestion(sIdx, qIdx, q, answers, section.kind));
    });

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'dec-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'dec-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreDecimalExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('dec-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: DecimalExamWrong[] = [];
        let flat = 0;
        exam.sections.forEach((section) => {
          section.questions.forEach((q) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ question: q, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('dec-submit');
  submitBtn.setAttribute('data-testid', 'dec-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  doneBtn.classList.add('dec-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 圖示 + 作答:compare 選項按鈕、其餘文字輸入)。 */
function renderQuestion(
  sIdx: number,
  qIdx: number,
  q: DecimalQuestion,
  answers: DecimalExamAnswers,
  kind: DecimalSection['kind'],
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'dec-q';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:8px;padding:12px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';

  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  top.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;white-space:pre-wrap;';
  prompt.textContent = q.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  // 圖示(看圖 / 數線)。
  if (q.figure) {
    row.appendChild(renderFigure(q.figure));
  }

  answers[`${sIdx}:${qIdx}`] = '';

  if (kind === 'compare' && q.choices && q.choices.length > 0) {
    // 比大小:選項按鈕(點選即作答,單選)。
    const opts = document.createElement('div');
    opts.className = 'dec-choices';
    opts.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
    const buttons: HTMLButtonElement[] = [];
    q.choices.forEach((choice) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dec-choice';
      b.textContent = choice;
      b.style.cssText =
        'padding:6px 18px;font-size:18px;font-weight:700;border:1px solid var(--ef-line,#d8d2c4);' +
        'border-radius:999px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);cursor:pointer;';
      b.addEventListener('click', () => {
        answers[`${sIdx}:${qIdx}`] = choice;
        buttons.forEach((other) => {
          const sel = other === b;
          other.style.background = sel ? 'var(--ef-accent,#2f6b4f)' : 'var(--ef-paper-bright,#fff)';
          other.style.color = sel ? '#fff' : 'var(--ef-ink)';
          other.classList.toggle('dec-choice--selected', sel);
        });
      });
      buttons.push(b);
      opts.appendChild(b);
    });
    row.appendChild(opts);
  } else {
    // 文字輸入(互換 / 看圖 / 加減 / 應用)。
    const field = document.createElement('label');
    field.style.cssText =
      'display:flex;align-items:center;gap:6px;font-size:14px;color:var(--ef-sub);';
    const tag = document.createElement('span');
    tag.textContent = '答:';
    field.appendChild(tag);
    const input = document.createElement('input');
    input.type = 'text';
    input.className = `dec-input dec-input--${q.id}`;
    input.autocomplete = 'off';
    input.placeholder = kind === 'convert' && q.verify.op === 'dec-to-frac' ? '例:3/10' : '例:0.7';
    input.style.cssText =
      'min-width:8em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
      'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
    input.addEventListener('input', () => {
      answers[`${sIdx}:${qIdx}`] = input.value;
    });
    field.appendChild(input);
    row.appendChild(field);
  }

  return row;
}

/** 看圖 / 數線視覺(線上版):塗色格(10 格塗 N)/ 數線(0..maxInt 每大格 10 小格、箭頭指點)。 */
function renderFigure(figure: NonNullable<DecimalQuestion['figure']>): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'dec-figure';
  wrap.style.cssText = 'margin:4px 0 2px;';
  if (figure.kind === 'shade') {
    const grid = document.createElement('div');
    grid.className = 'dec-shade-grid';
    grid.setAttribute('data-testid', 'dec-shade-grid');
    grid.style.cssText = 'display:flex;border:1px solid var(--ef-ink,#333);width:fit-content;';
    for (let i = 0; i < figure.total; i++) {
      const cell = document.createElement('div');
      cell.style.cssText =
        'width:24px;height:36px;border-right:1px solid var(--ef-line,#bbb);' +
        (i < figure.shaded ? 'background:#7ba7d4;' : 'background:transparent;');
      if (i === figure.total - 1) cell.style.borderRight = 'none';
      grid.appendChild(cell);
    }
    wrap.appendChild(grid);
  } else {
    // 數線:SVG,0..maxInt,每大格 10 小格,箭頭指 tenths/10。
    const W = 320;
    const maxInt = figure.maxInt;
    const totalTicks = maxInt * 10;
    const x0 = 16;
    const x1 = W - 16;
    const span = x1 - x0;
    const px = (t: number): number => x0 + (span * t) / totalTicks;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(W));
    svg.setAttribute('height', '52');
    svg.setAttribute('data-testid', 'dec-numberline');
    const ns = 'http://www.w3.org/2000/svg';
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', String(x0));
    line.setAttribute('y1', '32');
    line.setAttribute('x2', String(x1));
    line.setAttribute('y2', '32');
    line.setAttribute('stroke', '#333');
    line.setAttribute('stroke-width', '1.5');
    svg.appendChild(line);
    for (let t = 0; t <= totalTicks; t++) {
      const big = t % 10 === 0;
      const tick = document.createElementNS(ns, 'line');
      const x = px(t);
      tick.setAttribute('x1', String(x));
      tick.setAttribute('y1', String(big ? 24 : 28));
      tick.setAttribute('x2', String(x));
      tick.setAttribute('y2', String(big ? 40 : 36));
      tick.setAttribute('stroke', '#333');
      tick.setAttribute('stroke-width', big ? '1.5' : '0.8');
      svg.appendChild(tick);
      if (big) {
        const lab = document.createElementNS(ns, 'text');
        lab.setAttribute('x', String(x));
        lab.setAttribute('y', '50');
        lab.setAttribute('text-anchor', 'middle');
        lab.setAttribute('font-size', '11');
        lab.setAttribute('fill', '#333');
        lab.textContent = String(t / 10);
        svg.appendChild(lab);
      }
    }
    // 箭頭(指定點)。
    const ax = px(figure.tenths);
    const arrow = document.createElementNS(ns, 'path');
    arrow.setAttribute('d', `M${ax} 8 L${ax - 5} 0 L${ax + 5} 0 Z`);
    arrow.setAttribute('fill', '#b03030');
    const stem = document.createElementNS(ns, 'line');
    stem.setAttribute('x1', String(ax));
    stem.setAttribute('y1', '8');
    stem.setAttribute('x2', String(ax));
    stem.setAttribute('y2', '32');
    stem.setAttribute('stroke', '#b03030');
    stem.setAttribute('stroke-width', '1.5');
    svg.appendChild(stem);
    svg.appendChild(arrow);
    wrap.appendChild(svg);
  }
  return wrap;
}

/** 結果區:總分 + 逐題對錯 + 正解。 */
function renderResult(el: HTMLElement, result: DecimalExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'dec-score';
  headline.setAttribute('data-testid', 'dec-score');
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'dec-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';
  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `dec-result-line ${r.correct ? 'dec-correct' : 'dec-wrong'}`;
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    line.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${r.correctLabel}`
      : `${mark} 第 ${idx + 1} 題:你寫「${chosen}」→ 正解「${r.correctLabel}」`;
    list.appendChild(line);
  });
  el.appendChild(list);
}
